"use client";
import { useEffect, useRef } from "react";
import { useNativeActions } from "@/utils/useNativeActions";
import { useCookies } from "react-cookie";
import { usePathname } from "next/navigation";
import { RestaurantCart, RetailCart, ServiceCart } from "@/models/cart";
import { determineBackAction } from "@/utils/nativeBridgeUtils";
import { getItemId } from "@/utils/itemUrlUtils";

let pathStack: string[] = [];

export const getAppHistoryCount = () => pathStack.length;
export const getPreviousPath = () => pathStack.at(-2);
export const getPathTwoStepsBack = () => pathStack.at(-3);

export const anchorHistoryToHome = () => {
  pathStack = ["/"];
};
export const NativeSideEffectManager = () => {
  const {
    syncCartContext,
    isCmaApp,
    renewSession,
    enterHomePage,
    leaveHomePage,
    merchant,
  } = useNativeActions();
  const isSimpleStorefront = merchant?.merchantMeta?.selectedSetup === "Simple";
  const [cookies, setCookie, removeCookie] = useCookies([
    "crossMerchantCartContext",
    "pendingNativeRoute",
  ]);
  const currentMerchantId = merchant?.id;
  const currentMerchantSlug = merchant?.slug;
  const globalOwner = cookies.crossMerchantCartContext;

  const pathname = usePathname();

  const wasOnHomePage = useRef<boolean | null>(null);
  /*
   * Handles route tracking for CMA
   */
  useEffect(() => {
    if (pathStack.at(-1) === pathname) {
      return;
    }

    if (pathStack.length > 1 && pathStack.at(-2) === pathname) {
      pathStack.pop();
      return;
    }

    const prevPath = pathStack.at(-1) || "";
    const wasOnItemPage = !!getItemId(prevPath);
    const isMenuPage =
      pathname.endsWith("/all") ||
      pathname === "/shop" ||
      pathname.endsWith("/menu");

    if (wasOnItemPage && isMenuPage) {
      pathStack[pathStack.length - 1] = pathname;
      return;
    }

    const currentItemId = getItemId(pathname);
    const prevItemId = getItemId(prevPath);

    if (currentItemId && prevItemId && currentItemId === prevItemId) {
      pathStack[pathStack.length - 1] = pathname;
    } else {
      pathStack.push(pathname);
    }
  }, [pathname]);

  /*
   * Handles session renewal
   */
  useEffect(() => {
    if (isCmaApp && pathname === "/expired") {
      renewSession();
    }
  }, [pathname, isCmaApp, renewSession]);

  const isGiftCardOnly = !!merchant?.isStandaloneGiftCardWebsite;

  /*
   * Handle entering/leaving homepage
   */
  useEffect(() => {
    if (!isCmaApp) return;

    const isCurrentlyHomePage =
      determineBackAction(pathname, !!isSimpleStorefront, isGiftCardOnly) ===
      "HIDE_WEB_VIEW";

    if (isCurrentlyHomePage && wasOnHomePage.current !== true) {
      enterHomePage();
      wasOnHomePage.current = true;
    } else if (!isCurrentlyHomePage && wasOnHomePage.current === true) {
      leaveHomePage();
      wasOnHomePage.current = false;
    }
  }, [
    pathname,
    isCmaApp,
    isSimpleStorefront,
    enterHomePage,
    leaveHomePage,
    isGiftCardOnly,
  ]);

  /*
   * Handles global cross merchant cart clearing
   */
  useEffect(() => {
    if (!isCmaApp) return;

    if (globalOwner && currentMerchantId && globalOwner !== currentMerchantId) {
      RetailCart.clear();
      ServiceCart.clear();
      RestaurantCart.clear();

      globalThis.dispatchEvent(new Event("storage"));
    }
  }, [currentMerchantId, globalOwner, isCmaApp]);

  /*
   * Handles cart syncing with native app
   */

  useEffect(() => {
    if (!isCmaApp) return;

    const handleCartUpdate = (event: CustomEvent) => {
      const cartItems = event.detail;

      const itemsLength = Array.isArray(cartItems) ? cartItems.length : 0;

      if (itemsLength > 0 && currentMerchantId) {
        setCookie("crossMerchantCartContext", currentMerchantId, {
          path: "/",
          maxAge: 604800,
        });
      } else if (itemsLength === 0) {
        removeCookie("crossMerchantCartContext", { path: "/" });
      }

      syncCartContext(cartItems);
    };

    globalThis.addEventListener(
      "local-cart-updated",
      handleCartUpdate as EventListener
    );

    return () => {
      globalThis.removeEventListener(
        "local-cart-updated",
        handleCartUpdate as EventListener
      );
    };
  }, [
    syncCartContext,
    isCmaApp,
    currentMerchantId,
    currentMerchantSlug,
    setCookie,
    removeCookie,
  ]);

  /*
   * Handles roll back cart on current merchant
   */
  useEffect(() => {
    if (!isCmaApp) return;

    const handleRollback = () => {
      RetailCart.clear();
      ServiceCart.clear();
      RestaurantCart.clear();
      removeCookie("crossMerchantCartContext", { path: "/" });

      globalThis.dispatchEvent(new Event("storage"));
    };

    globalThis.addEventListener("RollbackCart", handleRollback);

    return () => {
      globalThis.removeEventListener("RollbackCart", handleRollback);
    };
  }, [isCmaApp, removeCookie]);

  return null;
};
